import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useTour } from './store'
import { waitForElement } from './waitForElement'

type Field = HTMLInputElement | HTMLTextAreaElement

/**
 * The fillable fields reached by a selector.
 *
 * The selector can point at the real input or at whatever wraps it — a text field is often a
 * `div` with an `input` inside, and whoever writes a tour naturally points at the element they see
 * highlighted. Requiring the exact shape would mean a step that never advances, without saying why.
 */
const inputsWithin = (selector: string): Field[] =>
  [...document.querySelectorAll<HTMLElement>(selector)].flatMap((node) =>
    node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
      ? [node]
      : [...node.querySelectorAll<Field>('input, textarea')],
  )

/**
 * The guided tour's engine: gets the user to the step, wherever it lives.
 *
 * A tour library lights up an element on the current page and stops there. A real tour crosses
 * routes and drawers, so the part that matters is the sequence:
 *
 *   change route if needed -> wait for the target to exist -> light it up
 *
 * The glow itself is drawn by `TourSpotlight`; this only decides WHICH element is the target and
 * publishes it to the store.
 *
 * Mounted once, above the router content — it is the one thing that must survive the page changes
 * it causes itself.
 */
export const useTourRunner = (): void => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { tour, index, next, previous, stop, setWaiting, setTarget, waiting, rewound } = useTour()

  /** The step being prepared: when the wait ends, check the user hasn't moved on already. */
  const pendingRef = useRef(0)

  /**
   * The step already navigated for.
   *
   * This effect also reruns when `pathname` changes — it has to, to find the target again after
   * the route change — and without this mark the navigation would repeat every time: opening
   * another page mid-tour would bounce straight back. The tour accompanies, it does not hold on —
   * if the user leaves, the step is left without a target and the panel says so.
   */
  const navigatedForRef = useRef<string | null>(null)

  useEffect(() => {
    const step = tour?.steps[index]
    if (!tour || !step) {
      setTarget(null)
      navigatedForRef.current = null
      return
    }

    const token = ++pendingRef.current
    setWaiting(true)

    // Navigate only when the step lives elsewhere, and only once per step: renavigating on the
    // same route would remount the page and make the just-found target disappear.
    const stepKey = `${tour.id}:${index}`
    if (step.route && step.route !== pathname && navigatedForRef.current !== stepKey) {
      navigatedForRef.current = stepKey
      navigate(step.route)
    }

    let cancelled = false

    const show = async (): Promise<void> => {
      // The tour opens the context drawer itself: wait for the opener to exist (the page may have
      // just changed) and click it, but only if it is not open already — a second click would
      // close it. `step.target` must be checked first — `querySelector("")` throws, not returns
      // null.
      const alreadyOpen = step.target ? Boolean(document.querySelector(step.target)) : false

      if (step.openWith && !alreadyOpen) {
        const opener = await waitForElement(step.openWith, step.waitMs ?? 8000)
        if (cancelled || token !== pendingRef.current) return
        opener?.click()
      }

      const element = step.target ? await waitForElement(step.target, step.waitMs ?? 8000) : null

      if (cancelled || token !== pendingRef.current) return
      setWaiting(false)
      // The target never arrived: publish null and the step still reads, in the panel. Better an
      // instruction with no glow than a stuck tour.
      setTarget(element)
    }

    void show()

    return () => {
      cancelled = true
    }
  }, [tour, index, pathname, navigate, setWaiting, setTarget])

  // Advance on appearance: waits for the RESULT of the action (the drawer opening), not the
  // gesture. So the tour never races ahead when a click produced nothing, and recognises the
  // action however it was actually done.
  useEffect(() => {
    const step = tour?.steps[index]
    // Nothing is observed until the step is actually on screen: during the wait we are still on
    // the PREVIOUS screen, and a condition that is already true there (a button that will be
    // locked on the new one) would slip the step away before it is ever shown.
    if (waiting || rewound || !step?.advanceWhen) return undefined

    let cancelled = false
    void waitForElement(step.advanceWhen, 120000).then((element) => {
      if (!cancelled && element) next()
    })

    return () => {
      cancelled = true
    }
  }, [tour, index, next, waiting, rewound])

  // Advance on disappearance: the result of the action is a closure. Something is saved and the
  // drawer closes — and that, not the list behind it reappearing, is what says the action worked.
  useEffect(() => {
    const step = tour?.steps[index]
    const selector = step?.advanceWhenGone
    if (waiting || rewound || !selector) return undefined

    // No mutation observer: this waits for a node that no longer exists, and an observer would
    // have to be attached to a parent that could disappear along with it. A read every half second
    // costs nothing and has no node to hold onto.
    const timer = setInterval(() => {
      if (!document.querySelector(selector)) next()
    }, 500)

    return () => clearInterval(timer)
  }, [tour, index, next, waiting, rewound])

  // Advance on filled: waits for something to be written, then moves to what to do with it.
  useEffect(() => {
    const step = tour?.steps[index]
    const selector = step?.advanceWhenFilled
    if (waiting || rewound || !selector) return undefined

    const filled = (): boolean => {
      const fields = inputsWithin(selector)
      // No field found = a selector that hits nothing. Treated as "not ready yet", NEVER as
      // "everything filled": `every` on an empty list is true, and that alone would slip the step
      // away.
      return fields.length > 0 && fields.every((field) => field.value.trim() !== '')
    }

    if (filled()) {
      // Already filled on arrival (the form was pre-filled): nobody is asked to retype what is
      // there. Going back does not pass through here — `rewound` above handles that — otherwise
      // "Back" on a full form would bounce straight forward again.
      next()
      return undefined
    }

    const check = (): void => {
      if (filled()) next()
    }

    document.addEventListener('input', check, true)
    document.addEventListener('change', check, true)
    // Safety net: a value set programmatically emits no `input`, and a field that appears later
    // (the drawer still opening) has nothing announcing it. The check costs one `.value` read
    // every half second, only while the step is open.
    const timer = setInterval(check, 500)

    return () => {
      document.removeEventListener('input', check, true)
      document.removeEventListener('change', check, true)
      clearInterval(timer)
    }
  }, [tour, index, next, waiting, rewound])

  // Advance on click: where the step exists to make the action HAPPEN, a "Next" button would be a
  // shortcut past the very thing to learn.
  useEffect(() => {
    const step = tour?.steps[index]
    if (waiting || !step?.advanceOnClick || !step.target) return undefined

    const onClick = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null
      if (target?.closest(step.target as string)) next()
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
    // `rewound` is not needed here: a click is a gesture, not a state of the page, and cannot come
    // out "already true" by going back.
  }, [tour, index, next, waiting])

  // Arrows and Escape: someone following a tour has their hands on the keyboard.
  useEffect(() => {
    if (!tour) return undefined

    const onKey = (event: KeyboardEvent): void => {
      // Inside a field the arrows move the cursor: intercepting them would skip a step on someone
      // who is simply correcting what they typed.
      const focused = document.activeElement
      const typing =
        focused instanceof HTMLInputElement ||
        focused instanceof HTMLTextAreaElement ||
        (focused instanceof HTMLElement && focused.isContentEditable)

      if (event.key === 'Escape') {
        stop()
        return
      }
      if (typing) return
      if (event.key === 'ArrowRight') next()
      if (event.key === 'ArrowLeft') previous()
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tour, next, previous, stop])
}
